import { fromBase64Url, fromCrockford, randomBytes, sha256, toBase64Url, toCrockford, utf8 } from './encoding.ts';

/**
 * Envelope encryption for an account (docs/ACCOUNTS-PLAN.md section 2).
 *
 * A random 256-bit master key encrypts everything the account syncs. The server keeps
 * only wrapped copies of it, one per unlock method ("slot"): the password (OPAQUE's
 * export key), each passkey that supports PRF, and the recovery key. Each wrap is
 * AES-256-GCM under a key derived with HKDF-SHA256. The user id and slot name are
 * authenticated data, so the server cannot pass one user's or slot's wrap off as another.
 *
 * Wrapped format: base64url of version(1) | iv(12) | ciphertext and tag.
 */
export const VAULT_FORMAT = 'realmery-vault-1';

const WRAP_VERSION = 1;
const MASTER_KEY_BYTES = 32;

export type UnlockMethod = 'password' | 'passkey' | 'recovery' | 'device';

/**
 * Where a wrap lives: `password`, `recovery` or `passkey:<credential id>` on the server,
 * or `device` in this browser only, for "Keep realms unlocked on this device".
 */
export type SlotKind = 'password' | 'recovery' | 'device' | `passkey:${string}`;

export interface Slot {
  readonly userId: string;
  readonly kind: SlotKind;
}

export class VaultError extends Error {
  constructor(
    readonly code: 'wrong_key' | 'invalid_recovery_key' | 'wrong_method' | 'malformed',
    message: string,
  ) {
    super(message);
    this.name = 'VaultError';
  }
}

/** A key that can wrap and unwrap the master key for one unlock method. */
export interface WrappingKey {
  readonly method: UnlockMethod;
  readonly key: CryptoKey;
}

export function slotMethod(kind: SlotKind): UnlockMethod {
  return kind.startsWith('passkey:') ? 'passkey' : (kind as 'password' | 'recovery' | 'device');
}

export function passkeySlot(credentialId: string): SlotKind {
  return `passkey:${credentialId}`;
}

async function hkdf(secret: Uint8Array<ArrayBuffer>, info: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: utf8(VAULT_FORMAT), info: utf8(info) },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function hkdfBits(secret: Uint8Array<ArrayBuffer>, info: string): Promise<Uint8Array<ArrayBuffer>> {
  const base = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: utf8(VAULT_FORMAT), info: utf8(info) },
    base,
    256,
  );
  return new Uint8Array(bits);
}

function slotData(slot: Slot): Uint8Array<ArrayBuffer> {
  return utf8(`${VAULT_FORMAT}\0${slot.userId}\0${slot.kind}`);
}

/** From OPAQUE's export key, which only a correct password produces. */
export async function passwordWrappingKey(exportKey: string): Promise<WrappingKey> {
  return { method: 'password', key: await hkdf(fromBase64Url(exportKey), 'wrap/password') };
}

/** From a passkey's PRF output for {@link PRF_SALT}. */
export async function passkeyWrappingKey(prfOutput: BufferSource): Promise<WrappingKey> {
  const view = ArrayBuffer.isView(prfOutput)
    ? new Uint8Array(prfOutput.buffer, prfOutput.byteOffset, prfOutput.byteLength)
    : new Uint8Array(prfOutput);
  const bytes = new Uint8Array(view);
  return { method: 'passkey', key: await hkdf(bytes, 'wrap/passkey') };
}

/**
 * A key this browser keeps for one account (docs/REALM-ENCRYPTION.md section 4): an
 * AES-256-GCM key created non-extractable, so it can be used here but never copied out.
 */
export async function deviceWrappingKey(key?: CryptoKey): Promise<WrappingKey> {
  return {
    method: 'device',
    key: key ?? ((await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])) as CryptoKey),
  };
}

/** The PRF input every Realmery passkey is evaluated with. Browsers hash it with a WebAuthn-specific prefix. */
export const PRF_SALT: Uint8Array<ArrayBuffer> = utf8('Realmery vault PRF salt, version 1');

/**
 * The account master key. It lives only in memory: it is never written to storage or
 * sent anywhere unwrapped.
 */
export class MasterKey {
  readonly #raw: Uint8Array<ArrayBuffer>;

  private constructor(raw: Uint8Array<ArrayBuffer>) {
    this.#raw = raw;
  }

  static generate(): MasterKey {
    return new MasterKey(randomBytes(MASTER_KEY_BYTES));
  }

  async wrap(slot: Slot, wrappingKey: WrappingKey): Promise<string> {
    checkMethod(slot, wrappingKey);
    const iv = randomBytes(12);
    const sealed = new Uint8Array(
      await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: slotData(slot) }, wrappingKey.key, this.#raw),
    );
    const out = new Uint8Array(1 + iv.length + sealed.length);
    out[0] = WRAP_VERSION;
    out.set(iv, 1);
    out.set(sealed, 1 + iv.length);
    return toBase64Url(out);
  }

  static async unwrap(wrapped: string, slot: Slot, wrappingKey: WrappingKey): Promise<MasterKey> {
    checkMethod(slot, wrappingKey);
    let bytes: Uint8Array<ArrayBuffer>;
    try {
      bytes = fromBase64Url(wrapped);
    } catch {
      throw new VaultError('malformed', 'The stored key is damaged.');
    }
    if (bytes.length !== 1 + 12 + MASTER_KEY_BYTES + 16 || bytes[0] !== WRAP_VERSION) {
      throw new VaultError('malformed', 'The stored key is damaged or from a newer version.');
    }
    try {
      const raw = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: bytes.slice(1, 13), additionalData: slotData(slot) },
        wrappingKey.key,
        bytes.slice(13),
      );
      return new MasterKey(new Uint8Array(raw));
    } catch {
      throw new VaultError('wrong_key', 'That key does not unlock this account.');
    }
  }

  /** A non-extractable key for one purpose, such as wrapping realm keys (Phase 3). */
  deriveKey(purpose: string): Promise<CryptoKey> {
    return hkdf(this.#raw, `purpose/${purpose}`);
  }
}

function checkMethod(slot: Slot, wrappingKey: WrappingKey): void {
  if (slotMethod(slot.kind) !== wrappingKey.method) {
    throw new VaultError('wrong_method', `A ${wrappingKey.method} key cannot open the ${slot.kind} slot.`);
  }
}

// ---- recovery key ----

const RECOVERY_RANDOM_BYTES = 18; // 144 bits
const RECOVERY_CHECK_BYTES = 2;

/**
 * A recovery key: 144 random bits plus a 16-bit checksum, as 32 Crockford base32
 * characters in groups of four (`7KQ2-…`). The checksum catches typing mistakes before
 * an unlock is attempted. It is high-entropy, so no slow password hashing is needed.
 */
export interface RecoveryKey {
  readonly text: string;
  readonly bytes: Uint8Array<ArrayBuffer>;
}

export async function generateRecoveryKey(): Promise<RecoveryKey> {
  const secret = randomBytes(RECOVERY_RANDOM_BYTES);
  const check = (await sha256(secret)).slice(0, RECOVERY_CHECK_BYTES);
  const all = new Uint8Array(RECOVERY_RANDOM_BYTES + RECOVERY_CHECK_BYTES);
  all.set(secret);
  all.set(check, RECOVERY_RANDOM_BYTES);
  const text = toCrockford(all).match(/.{4}/g)!.join('-');
  return { text, bytes: secret };
}

/** Accepts any case, spaces and dashes, and the look-alikes I, L (for 1) and O (for 0). */
export async function parseRecoveryKey(text: string): Promise<RecoveryKey> {
  const compact = text.replace(/[\s-]/g, '');
  const all = fromCrockford(compact, RECOVERY_RANDOM_BYTES + RECOVERY_CHECK_BYTES);
  if (!all) throw new VaultError('invalid_recovery_key', 'A recovery key is 32 letters and digits.');
  const secret = all.slice(0, RECOVERY_RANDOM_BYTES);
  const check = (await sha256(secret)).slice(0, RECOVERY_CHECK_BYTES);
  if (check[0] !== all[RECOVERY_RANDOM_BYTES] || check[1] !== all[RECOVERY_RANDOM_BYTES + 1]) {
    throw new VaultError('invalid_recovery_key', 'That recovery key has a typo: check each group.');
  }
  return { text: toCrockford(all).match(/.{4}/g)!.join('-'), bytes: secret };
}

export async function recoveryWrappingKey(key: RecoveryKey): Promise<WrappingKey> {
  return { method: 'recovery', key: await hkdf(key.bytes, 'wrap/recovery') };
}

/**
 * Proves knowledge of the recovery key to the server without revealing it. The server
 * stores only {@link recoveryVerifier} of this value; neither can unwrap anything.
 */
export async function recoveryProof(key: RecoveryKey): Promise<string> {
  return toBase64Url(await hkdfBits(key.bytes, 'recovery/proof'));
}

/** What the server stores for a recovery key: SHA-256 of the proof. */
export async function recoveryVerifier(proof: string): Promise<string> {
  return toBase64Url(await sha256(fromBase64Url(proof)));
}
